import { FC, useMemo, useRef } from 'react';
import { Game } from '../../games.model';
import { TileRowCollection, SectionConfig, GridWidth } from '../../GameCard/GameCard.types';
import { TileRowSection } from '../TileRowSection';
import { createGamesById, createGameFinder } from '../utils';
import { useBreakpointInfo, MediaBreakpoint } from '../../../../hooks/useDeviceInfo';
import { useMobileSlideCreator } from '../MobileSlideCreator';
import { useContainerWidth } from '../../../../hooks/useContainerWidth';

type SectionList = SectionConfig[];
const extractSections = (collection: TileRowCollection): SectionList => {
  const sections: SectionList = [];

  if (collection.half_width_tile) {
    sections.push(collection.half_width_tile);
  }

  if (collection.first_full_width_tile) {
    sections.push(collection.first_full_width_tile);
  }

  if (collection.second_full_width_tile) {
    sections.push(collection.second_full_width_tile);
  }

  if (collection.third_full_width_tile_block) {
    sections.push(collection.third_full_width_tile_block);
  }

  return sections;
};

const createFullWidthTileCollection = (
  idPrefix: string,
  labelBase: string,
  sections: SectionList,
  index: number
): TileRowCollection => ({
  id: `${idPrefix}-${index}`,
  SeeMoreViewAll: false,
  Width: GridWidth.FULL,
  half_width_tile: undefined,
  first_full_width_tile: sections[0],
  second_full_width_tile: sections[1],
  third_full_width_tile_block: sections[2],
  internal_name: index === 0 ? labelBase : `${labelBase} (${index + 1})`,
});

const mergeHalfWidthPair = (first: TileRowCollection, second?: TileRowCollection): TileRowCollection[] => {
  if (!second) {
    return [
      {
        ...first,
        id: `view-all-single-${first.id}`,
        Width: GridWidth.HALF,
        SeeMoreViewAll: false,
      },
    ];
  }

  const allSections = [...extractSections(first), ...extractSections(second)];

  if (allSections.length === 0) {
    return [];
  }

  const idPrefix = `view-all-merged-${first.id}-${second.id}`;
  const labelParts = [first.internal_name, second.internal_name].filter(Boolean) as string[];
  const labelBase = labelParts.length > 0 ? labelParts.join(' & ') : `View All ${first.id}-${second.id}`;
  const mergedCollections: TileRowCollection[] = [];
  const CHUNK_SIZE = 2;

  for (let i = 0; i < allSections.length; i += CHUNK_SIZE) {
    const sectionGroup = allSections.slice(i, i + CHUNK_SIZE);
    const slideIndex = Math.floor(i / CHUNK_SIZE);

    mergedCollections.push(createFullWidthTileCollection(idPrefix, labelBase, sectionGroup, slideIndex));
  }

  return mergedCollections;
};

const mergeHalfWidthCollections = (collections: TileRowCollection[]): TileRowCollection[] => {
  const merged: TileRowCollection[] = [];

  for (let i = 0; i < collections.length; i += 2) {
    const first = collections[i];
    const second = collections[i + 1];

    if (!first) {
      continue;
    }

    merged.push(...mergeHalfWidthPair(first, second));
  }

  return merged;
};

const transformCollectionsForViewAll = (collections: TileRowCollection[]): TileRowCollection[] => {
  const transformed: TileRowCollection[] = [];
  let pendingHalfWidth: TileRowCollection[] = [];

  const flushPending = (): void => {
    if (pendingHalfWidth.length === 0) {
      return;
    }

    transformed.push(...mergeHalfWidthCollections(pendingHalfWidth));
    pendingHalfWidth = [];
  };

  collections.forEach((collection) => {
    if (collection.Width === GridWidth.HALF) {
      pendingHalfWidth.push(collection);

      return;
    }

    flushPending();
    transformed.push(collection);
  });

  flushPending();

  return transformed;
};

interface ViewAllGamesGridContentProps {
  collections: TileRowCollection[];
  games: Game[];
  categoryName: string;
  disabled?: boolean;
  showGameName?: boolean;
  fallbackImageAltText?: string;
  fallbackImageUrl?: string;
  onViewGameInfo?: (game: Game) => void;
  onPlayDemo?: (game: Game) => void;
  onPlayGame?: (game: Game) => void;
  containerElementWidth?: number;
}
export const ViewAllGamesGridContent: FC<ViewAllGamesGridContentProps> = ({
  collections,
  games,
  categoryName,
  disabled = false,
  showGameName = true,
  fallbackImageAltText = '',
  fallbackImageUrl = '',
  onViewGameInfo,
  onPlayDemo,
  onPlayGame,
  containerElementWidth = 0,
}) => {
  const shouldUseMobileLayout = useBreakpointInfo([MediaBreakpoint.BELOW_MID_DESKTOP]);
  const contentRef = useRef<HTMLDivElement>(null);
  const measuredContainerWidth = useContainerWidth(contentRef);
  const effectiveContainerWidth = containerElementWidth || measuredContainerWidth;
  const normalizedCollections = useMemo(() => transformCollectionsForViewAll(collections), [collections]);
  const gamesById = useMemo(() => createGamesById(games), [games]);
  const findGameById = useMemo(() => createGameFinder(gamesById), [gamesById]);

  const sharedTileRowProps = useMemo(
    () => ({
      disabled,
      showGameName,
      fallbackImageAltText,
      fallbackImageUrl,
      onViewGameInfo,
      onPlayDemo,
      onPlayGame,
      containerElementWidth: effectiveContainerWidth,
    }),
    [
      disabled,
      showGameName,
      fallbackImageAltText,
      fallbackImageUrl,
      onViewGameInfo,
      onPlayDemo,
      onPlayGame,
      effectiveContainerWidth,
    ]
  );

  const { createMobileSlides } = useMobileSlideCreator({
    collectionsWithContent: normalizedCollections,
    findGameById,
    containerWidth: effectiveContainerWidth,
    disabled,
    showGameName,
    fallbackImageAltText,
    fallbackImageUrl,
    onViewGameInfo,
    onPlayDemo,
    onPlayGame,
  });

  const slides = useMemo(() => {
    if (shouldUseMobileLayout) {
      return createMobileSlides();
    }

    return normalizedCollections.map((config, index) => (
      <TileRowSection
        key={config.id || index}
        config={config}
        findGameById={findGameById}
        mode="view-all"
        {...sharedTileRowProps}
      />
    ));
  }, [shouldUseMobileLayout, normalizedCollections, findGameById, sharedTileRowProps, createMobileSlides]);

  return (
    <div ref={contentRef} className="view-all-games-grid__content" data-testid="view-all-games-grid-content">
      <div className="view-all-games-grid__slides">
        {slides.map((slide: JSX.Element, index: number) => {
          const slideKey = (slide.key as string | number | null | undefined) ?? `view-all-slide-${index}`;

          return (
            <div key={slideKey} className="view-all-games-grid__slide">
              {slide}
            </div>
          );
        })}
      </div>
    </div>
  );
};
